import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getProductsGroupOptions } from '@/modules/ProductGroup/services'
import { createServiceCost } from '@/modules/Report/components/ServiceCosts/services'
import { ModalProps } from '@/types/shared'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem, Typogaphy } from 'vnetwork-ui'
import * as yup from 'yup'

export interface CreateServiceCostModalProps extends ModalProps {
    refetch?: () => void
}

interface FormData {
    cost_year: number
    cost_month: number
    currency: string
    notes?: string
    description?: string
    products: Array<{
        product_group_name: string
        specific_amount: number
        shared_amount: number
    }>
}

const currentYear = dayjs().year()
const CreateServiceCostModal: React.FC<CreateServiceCostModalProps> = ({
    open,
    onClose,
    refetch,
}) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Fetch product groups for dropdown
    const { data: productGroupOptions } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.PRODUCT_GROUPS],
        queryFn: getProductsGroupOptions,
    })

    // Form schema
    const schema = useMemo(
        () =>
            yup.object().shape({
                notes: yup.string(),
                description: yup.string(),
                products: yup.array().of(
                    yup.object().shape({
                        product_group_name: yup.string().required(),
                        specific_amount: yup.number().min(0).required(),
                        shared_amount: yup.number().min(0).required(),
                    })
                ),
            }),
        [t]
    )

    // Initialize default values with all products
    const defaultValues: FormData = useMemo(
        () => ({
            cost_year: currentYear,
            cost_month: dayjs().month() + 1,
            currency: 'VND',
            notes: '',
            description: '',
            products: (productGroupOptions || []).map((option) => ({
                product_group_name: option.value,
                specific_amount: 0,
                shared_amount: 0,
            })),
        }),
        [productGroupOptions]
    )

    const formControl = useForm<FormData>({
        defaultValues,
        // @ts-ignore
        resolver: yupResolver(schema),
    })

    const { control, handleSubmit, reset } = formControl
    const { fields } = useFieldArray({
        control,
        name: 'products',
    })

    // Reset form when dialog opens/closes or productGroupOptions change
    useEffect(() => {
        if (open && productGroupOptions) {
            reset({
                cost_year: currentYear,
                cost_month: dayjs().month() + 1,
                currency: 'VND',
                notes: '',
                description: '',
                products: productGroupOptions.map((option) => ({
                    product_group_name: option.value,
                    specific_amount: 0,
                    shared_amount: 0,
                })),
            })
        }
    }, [open, productGroupOptions, reset])

    const onSubmit = async (values: FormData) => {
        try {
            setIsSubmitting(true)
            const promises: Promise<any>[] = []

            // Loop through all products and create costs
            values?.products?.forEach((product) => {
                // Create specific cost if amount > 0
                if (product?.specific_amount) {
                    promises.push(
                        createServiceCost({
                            cost_type: 'specific',
                            product_group_name: product.product_group_name,
                            cost_month: `${values.cost_year}-${values.cost_month}`,
                            cost_amount: product.specific_amount,
                            currency: values.currency,
                            notes: values.notes,
                            description: values.description,
                        })
                    )
                }

                // Create shared cost if amount > 0
                if (product?.shared_amount > 0) {
                    promises.push(
                        createServiceCost({
                            cost_type: 'shared',
                            product_group_name: product.product_group_name,
                            cost_month: `${values.cost_year}-${values.cost_month}`,
                            cost_amount: product.shared_amount,
                            currency: values.currency,
                            notes: values.notes,
                            description: values.description,
                        })
                    )
                }
            })

            // Check if there's at least one cost to create
            if (promises.length === 0) {
                toast.error(t('at_least_one_amount_required'))
                setIsSubmitting(false)
                return
            }

            // Execute all API calls
            await Promise.all(promises)

            // Invalidate queries
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.REPORTS_BI_SERVICE_COSTS],
            })
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.REPORTS_BI_REVENUE_COST],
            })

            // Show success message
            toast.success(t('create-success'))

            // Refetch and close
            refetch?.()
            onClose()
        } catch (error) {
            toast.error(t('something went wrong'))
            console.error('Failed to create service costs:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('create_service_cost_bulk')}
            maxWidth="lg"
        >
            <form
                onSubmit={handleSubmit(onSubmit as any)}
                className="space-y-4 p-4"
            >
                {/* Basic Info Section */}
                <div className="grid grid-cols-2 gap-4">
                    {/* Cost Month */}
                    <FormItem
                        control={control}
                        name="cost_year"
                        type="select"
                        label={t('cost_year')}
                        options={Array.from({ length: 12 }, (_, index) => ({
                            label: `${t('year')} ${currentYear - index}`,
                            value: currentYear - index,
                        }))}
                        allowClear={false}
                    />
                    <FormItem
                        control={control}
                        name="cost_month"
                        type="select"
                        label={t('cost_month')}
                        options={Array.from({ length: 12 }, (_, index) => ({
                            label: `${t('month')} ${index + 1}`,
                            value: index + 1,
                        }))}
                        allowClear={false}
                    />
                </div>

                {/* Products Table */}
                <div className="space-y-2">
                    <Typogaphy className="text-base font-medium text-primary">
                        {t('product_costs')}
                    </Typogaphy>
                    <div className="max-h-[400px] overflow-y-auto border rounded-lg">
                        <table className="w-full">
                            <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">
                                        {t('product_group_name')}
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">
                                        {t('specific_cost_amount')}
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">
                                        {t('shared_cost_amount')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {fields.map((field, index) => (
                                    <tr
                                        key={field.id}
                                        className="border-b hover:bg-gray-50"
                                    >
                                        <td className="px-4 py-3 text-sm text-gray-900">
                                            {field.product_group_name}
                                        </td>
                                        <td className="px-4 py-3">
                                            <FormItem
                                                control={control}
                                                name={`products.${index}.specific_amount`}
                                                type="number"
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <FormItem
                                                control={control}
                                                name={`products.${index}.shared_amount`}
                                                type="number"
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Notes and Description */}
                <div className="grid grid-cols-1 gap-4">
                    <FormItem
                        control={control}
                        name="notes"
                        type="textarea"
                        label={t('notes')}
                    />

                    <FormItem
                        control={control}
                        name="description"
                        type="textarea"
                        label={t('description')}
                    />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        loading={isSubmitting}
                    >
                        {t('create_all')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default CreateServiceCostModal
